import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useBlocker } from "react-router-dom";
import { useAuth } from "./AuthContext";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "../components/ui/alert-dialog";

const NavigationGuardContext = createContext(null);

export const NavigationGuardProvider = ({ children }) => {
    const { user } = useAuth();
    const [dirty, setDirty] = useState(false);
    const [pendingAction, setPendingAction] = useState(null);
    const bypass = useRef(false);
    const handledBlocker = useRef(null);
    const leaving = useRef(false);
    const blocker = useBlocker(useCallback(({ currentLocation, nextLocation }) => (
        Boolean(user) && dirty && !bypass.current && currentLocation.pathname !== nextLocation.pathname
    ), [dirty, user]));
    const latestBlocker = useRef(blocker);
    latestBlocker.current = blocker;

    // React can run the save/expiry effect before a POP navigation has finished
    // committing. Resolve each blocked transition exactly once, even if a click
    // and an effect see the same still-"blocked" snapshot.
    const resolveBlocker = useCallback((decision) => {
        const current = latestBlocker.current;
        if (current.state !== "blocked" || handledBlocker.current === current) return;
        handledBlocker.current = current;
        current[decision]();
    }, []);

    useEffect(() => {
        if (!dirty || !user) return undefined;
        const warn = (event) => { event.preventDefault(); event.returnValue = ""; };
        window.addEventListener("beforeunload", warn);
        return () => window.removeEventListener("beforeunload", warn);
    }, [dirty, user]);

    useEffect(() => {
        if (!user) {
            setDirty(false);
            setPendingAction(null);
            // An expired session must never wait for an unsaved-edit decision.
            resolveBlocker("proceed");
        } else if (!dirty) resolveBlocker("proceed");
    }, [user, dirty, blocker, resolveBlocker]);

    const requestAction = useCallback((action) => {
        if (dirty && user) setPendingAction(() => action);
        else action();
    }, [dirty, user]);

    const stay = () => {
        setPendingAction(null);
        resolveBlocker("reset");
    };
    const leave = async () => {
        if (leaving.current) return;
        leaving.current = true;
        bypass.current = true;
        setDirty(false);
        const action = pendingAction;
        setPendingAction(null);
        resolveBlocker("proceed");
        try { if (action) await action(); } finally {
            bypass.current = false;
            leaving.current = false;
        }
    };

    return (
        <NavigationGuardContext.Provider value={{ setDirty, requestAction }}>
            {children}
            {user && <AlertDialog open={blocker.state === "blocked" || !!pendingAction} onOpenChange={(open) => { if (!open) stay(); }}>
                <AlertDialogContent data-testid="unsaved-changes-dialog" className="portal-confirm-dialog" overlayClassName="z-[100]" overlayTestId="unsaved-dialog-overlay">
                    <AlertDialogHeader>
                        <AlertDialogTitle data-testid="unsaved-changes-title">Leave without saving?</AlertDialogTitle>
                        <AlertDialogDescription data-testid="unsaved-changes-description">You have unfinished changes. Stay here to save them, or leave and discard your edits.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="unsaved-stay-button" onClick={(event) => { event.preventDefault(); stay(); }}>Keep editing</AlertDialogCancel>
                        <AlertDialogAction data-testid="unsaved-leave-button" onClick={(event) => { event.preventDefault(); leave(); }}>Discard & leave</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>}
        </NavigationGuardContext.Provider>
    );
};

export const useNavigationGuard = () => useContext(NavigationGuardContext);

export const useUnsavedChanges = (dirty) => {
    const { setDirty } = useNavigationGuard();
    useEffect(() => {
        setDirty(Boolean(dirty));
        return () => setDirty(false);
    }, [dirty, setDirty]);
};